import { css } from '@emotion/react'
import { observer } from 'mobx-react'
import type { FC } from 'react'
import { Link } from 'react-router-dom'

import { tJosa } from '~/@app/i18n'
import { colors, colorTheme, fontWeight, textEllipsis, typo } from '~/@common/styles'
import { examLearningContentStatusText, learningContentStatusText } from '~/@common/types'
import Badge, { type Props as BadgeProps } from '~/@common/ui/Badge/Badge'
import { Icon } from '~/@common/ui/Icon/Icon'

import type RecentStudy from '../../@common/model/RecentStudy'
import {
  type RecentStudyType,
  recentStudyTypeMenuText,
  recentStudyTypeText,
} from '../../@common/types'
import { buildStudyListCardHref, handleStudyCardClick } from '../StudyList/studyCardNav'

type Props = {
  item: RecentStudy
  /** 현재 활성 탭 — mixpanel 트래킹의 `탭_이름` 에 사용 */
  activeType: RecentStudyType
}

type MetaItem = {
  text: string
  isPrimaryTheme?: boolean
}

const StudyCard: FC<Props> = ({ item, activeType }) => {
  const href = buildStudyListCardHref(item)
  const isKMM = item.examType === 'KMM' || item.examType === 'KMM_PRACTICE_TEST'
  const iconName = item.type === 'WORKBOOK' ? 'icon_book_read' : 'icon_description_paper'

  // 하단 메타 라인: 숙제 / 페이지 범위 / 문제 수 / 점수 or 채점 비공개
  const textItems: MetaItem[] = []

  if (item.existsHomework) {
    textItems.push({ text: '숙제' })
  }

  if (item.workbookPagesText) {
    textItems.push({ text: item.workbookPagesText })
  }

  if (item.problemsCount > 0) {
    textItems.push({ text: `${item.problemsCount}문제` })
  }

  // KMM 완료했지만 스케줄이 아직 RESOLVED 전 → 채점 비공개 표시
  if (isKMM && item.status === 'COMPLETE' && item.examScheduleProgress !== 'RESOLVED') {
    textItems.push({ text: '채점 비공개', isPrimaryTheme: true })
  }

  // KMM 외 & 점수 노출 조건 (COMPLETE + EXAM/WORKSHEET) 충족 시 점수 표시
  if (!isKMM && item.score != null) {
    textItems.push({ text: `점수 ${item.score}점`, isPrimaryTheme: true })
  }

  // 진행 상태 뱃지 컬러 테마
  let progressColorTheme: BadgeProps['theme']
  switch (item.status) {
    case 'INCOMPLETE':
      progressColorTheme = 'primaryBlue'
      break
    case 'COMPLETE':
      progressColorTheme = 'secondaryGray'
      break
    case 'PROGRESS':
      progressColorTheme = 'primaryGray'
      break
  }

  const _CardType = () => {
    // TODO_EC: ACADEMY_GROUP(학원그룹 시험) = 와이즈만 전국 수학학력평가 => i18n(/language-pack) 으로 프랜차이즈 이름 표기 필요
    if (item.examType === 'ACADEMY_GROUP') {
      return <>와이즈만 전국 수학학력평가</>
    }

    if (isKMM) {
      return <>{tJosa(recentStudyTypeText[item.type])}</>
    }

    return (
      <>
        {tJosa(recentStudyTypeText[item.type])}
        <span className="dot">・</span>
        {item.schoolTypeGrade}
        {item.revision === 'CURRICULUM_22' && '(22개정)'}
      </>
    )
  }

  return (
    <li css={_Style}>
      <Link
        to={href}
        onClick={(e) => handleStudyCardClick(item, e, recentStudyTypeMenuText[activeType])}
      >
        <p className="badges">
          <Icon name={iconName} size={12} css={{ color: colorTheme.primary }} />
          <span className="card-type">
            <_CardType />
          </span>
          {item.isPrivate && <Badge>비공개</Badge>}
          <Badge theme={progressColorTheme}>
            {item.type === 'EXAM' || item.type === 'CAS'
              ? examLearningContentStatusText[item.status]
              : learningContentStatusText[item.status]}
          </Badge>
          {item.studentWorkbookRevisionRound && (
            <Badge>{item.studentWorkbookRevisionRound}회차</Badge>
          )}
          {item.isAutoScored && <Badge>자동 채점</Badge>}
        </p>
        <p className="title">{item.title}</p>
        {textItems.length > 0 && (
          <p className="meta">
            {textItems.map((meta, index) => (
              <span
                key={index}
                className="meta-item"
                style={{
                  color: meta.isPrimaryTheme ? colorTheme.primary : undefined,
                }}
              >
                {meta.text}
              </span>
            ))}
          </p>
        )}
      </Link>
    </li>
  )
}

export default observer(StudyCard)

const _Style = css`
  background-color: ${colors.white};
  border-radius: var(--Radius-300);
  display: flex;
  flex-direction: column;
  gap: 6px;

  .margin-left-auto {
    margin-left: auto;
  }

  > a {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 17px 20px;
  }
  .title {
    width: 100%;
    ${textEllipsis(2)};
    color: var(--Colors-Gray-900);
  }
  .card-type {
    display: flex;
    align-items: center;
    ${typo.caption01};
    color: ${colors.blue.$500};
    font-weight: ${fontWeight.bold};

    .dot {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 12px;
    }
  }
  .badges {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .meta {
    ${typo.caption01};
    color: var(--Colors-Gray-700);
  }
  .meta-item {
    position: relative;
    &:before {
      content: '';
      display: inline-block;
      width: 1px;
      height: 10px;
      margin: 0 6px;
      background-color: var(--Colors-Gray-500);
    }
    &:first-of-type:before {
      display: none;
    }
  }
`
